import { ImagePlus, X } from 'lucide-react';
import {
  type ChangeEvent,
  type FormEvent,
  type KeyboardEventHandler,
  type MutableRefObject,
} from 'react';

import { type PreparedChatImage } from '../../utils/chat-image';

interface PlaygroundComposerProps {
  message: string;
  onMessageChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onKeyDown: KeyboardEventHandler<HTMLTextAreaElement>;
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>;
  pendingImage: PreparedChatImage | null;
  onClearPendingImage: () => void;
  imageInputRef: MutableRefObject<HTMLInputElement | null>;
  onImageFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  isPreparingImage: boolean;
  isFieldDisabled: boolean;
  isSendDisabled: boolean;
  isStreaming: boolean;
}

export const PlaygroundComposer = ({
  message,
  onMessageChange,
  onSubmit,
  onKeyDown,
  textareaRef,
  pendingImage,
  onClearPendingImage,
  imageInputRef,
  onImageFileChange,
  isPreparingImage,
  isFieldDisabled,
  isSendDisabled,
  isStreaming,
}: PlaygroundComposerProps) => (
  <form onSubmit={onSubmit} className="border-t border-black/10 bg-white px-5 py-4">
    {pendingImage ? (
      <div className="mb-3 flex items-center gap-3">
        <div className="relative">
          <img
            src={pendingImage.dataUrl}
            alt="Bild som bifogas"
            className="h-16 w-16 rounded-sm border border-black/10 object-cover"
          />
          <button
            type="button"
            onClick={onClearPendingImage}
            aria-label="Ta bort bilden"
            className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-black/10 bg-white text-brand-blue shadow-sm transition-colors hover:bg-brand-beige-tint-2"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        <p className="text-xs text-text-grey">
          Bilden skickas med ditt nästa meddelande — skriv gärna vad du vill ha hjälp med.
        </p>
      </div>
    ) : null}
    <div className="flex items-end gap-3">
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onImageFileChange}
      />
      <button
        type="button"
        onClick={() => imageInputRef.current?.click()}
        disabled={isFieldDisabled || isPreparingImage}
        title="Bifoga en bild på en produkt (plagg, skor, accessoarer m.m.)"
        aria-label="Bifoga bild"
        className="flex items-center justify-center rounded-sm border border-black/10 bg-white px-3 text-brand-blue transition-colors hover:border-brand-blue disabled:cursor-not-allowed disabled:bg-brand-beige-tint-2 disabled:opacity-70"
      >
        {isPreparingImage ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-blue/30 border-t-brand-blue" />
        ) : (
          <ImagePlus className="h-5 w-5" />
        )}
      </button>
      <textarea
        ref={textareaRef}
        rows={1}
        value={message}
        onChange={(event) => onMessageChange(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Skriv ditt meddelande..."
        disabled={isFieldDisabled}
        className="flex-1 resize-none overflow-y-auto rounded-sm border border-black/10 bg-white px-4 py-3 text-base text-brand-blue placeholder:text-text-grey transition-colors focus:border-brand-blue focus:outline-none disabled:cursor-not-allowed disabled:bg-brand-beige-tint-2 disabled:opacity-70"
      />
      <button
        type="submit"
        disabled={isSendDisabled}
        className="min-w-[110px] rounded-sm border border-brand-blue bg-brand-blue px-5 py-3 text-xs uppercase tracking-wider text-white transition-colors duration-200 hover:bg-white hover:text-brand-blue disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isStreaming ? (
          <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          'Skicka'
        )}
      </button>
    </div>
  </form>
);
